import { Injectable } from '@angular/core';
import { HttpClient, HttpEvent } from '@angular/common/http';
import { Observable, throwError } from 'rxjs';
import { switchMap, catchError } from 'rxjs/operators';
import { environment } from '../../../environments/environment';

@Injectable({
  providedIn: 'root'
})
export class VersionesDocumentoService {
  private apiUrl = `${environment.apiUrl}/api/versiones-documento`;

  constructor(private http: HttpClient) { }

  // Crear una nueva versión de documento
  createVersion(data: any): Observable<any> {
    return this.http.post<any>(this.apiUrl, data);
  }

  // Obtener todas las versiones de documentos
  getAllVersiones(): Observable<any[]> {
    return this.http.get<any[]>(this.apiUrl);
  }

  // Obtener una versión de documento por ID
  getVersionById(id: number): Observable<any> {
    return this.http.get<any>(`${this.apiUrl}/${id}`);
  }

  // Obtener todas las versiones de un documento por documento_id
  getVersionesByDocumento(documentoId: number): Observable<any[]> {
    return this.http.get<any[]>(`${this.apiUrl}/documento/${documentoId}`);
  }

  // Obtener la versión vigente de un documento
  getVersionVigente(documentoId: number): Observable<any> {
    return this.http.get<any>(`${this.apiUrl}/documento/${documentoId}/vigente`);
  }

  // Actualizar una versión de documento por ID
  updateVersion(id: number, data: any): Observable<any> {
    return this.http.put<any>(`${this.apiUrl}/${id}`, data);
  }

  // Marcar una versión como vigente
  setVersionVigente(id: number): Observable<any> {
    return this.http.patch<any>(`${this.apiUrl}/${id}/set-vigente`, {});
  }

  // Eliminar una versión de documento por ID
  deleteVersion(id: number): Observable<any> {
    return this.http.delete<any>(`${this.apiUrl}/${id}`);
  }

  /**
   * Subir documento digital PESV usando el contrato del backend.
   * POST /api/digitales-pesv
   */
  uploadDigitalPesv(
    file: File,
    empresaNit: string,
    siglas: string,
  ): Observable<HttpEvent<any>> {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('empresaNit', empresaNit);
    formData.append('siglas', siglas);

    return this.http.post<any>(`${environment.apiUrl}/api/digitales-pesv`, formData, {
      reportProgress: true,
      withCredentials: true,
      observe: 'events',
    }).pipe(catchError((error: any) => {
      console.error('Error subiendo documento digital PESV:', error);
      return throwError(() => error);
    }));
  }

  /**
   * Descargar el documento digital PESV más reciente.
   * GET /api/digitales-pesv/:empresaNit/:siglas
   */
  downloadDigitalPesv(empresaNit: string, siglas: string): Observable<any> {
    const url = `${environment.apiUrl}/api/digitales-pesv/${encodeURIComponent(empresaNit)}/${encodeURIComponent(siglas)}`;

    return this.http.get(url, {
      withCredentials: true,
      responseType: 'blob',
      observe: 'response',
    }).pipe(catchError((error: any) => {
      console.error('Error descargando documento digital PESV:', error);
      return throwError(() => error);
    }));
  }

  /**
   * Descargar documento de versión renderizado (DOCX con placeholders reemplazados o PDF).
   * Backend renderiza con datos: empresa, NIT, código, versión, comité, etc.
   * 
   * @param versionId ID numérico de la versión (VersionesDocumento.id)
   * @param tipoSalida 'pdf' para PDF, 'word' para DOCX, undefined para lo que contenga
   * @returns Observable<HttpResponse> con blob (archivo renderizado)
   */
  descargarDocumentoVersionRendizado(
    versionId: number,
    tipoSalida?: 'pdf' | 'word',
    numeroComite?: number,
  ): Observable<any> {
    const params = new URLSearchParams();
    if (tipoSalida === 'pdf') {
      params.append('tipo', 'pdf');
    }

    if (numeroComite !== undefined && numeroComite !== null && !Number.isNaN(Number(numeroComite))) {
      params.append('numeroComite', String(numeroComite));
    }
    
    const url = `${this.apiUrl}/${versionId}/descargar${params.size ? '?' + params : ''}`;
    
    console.log('📥 Descargando documento rendizado:', url);

    return this.http.get(url, {
      withCredentials: true,
      responseType: 'blob',
      observe: 'response'
    });
  }

  /**
   * Obtener versión vigente por nombre de documento.
   * Requiere que el backend implemente: GET /api/versiones-documento/nombre/vigente?nombre_documento=...
   * 
   * @param nombreDocumento Nombre del documento (ej: "Liderazgo y Compromiso", "Acta de Comité")
   * @returns Observable<VersionesDocumento> con el ID y datos de la versión vigente
   */
  getVersionVigentePorNombre(nombreDocumento: string, numeroComite?: number): Observable<any> {
    const params = new URLSearchParams();
    params.append('nombre_documento', nombreDocumento);

    if (numeroComite !== undefined && numeroComite !== null && !Number.isNaN(Number(numeroComite))) {
      params.append('numeroComite', String(numeroComite));
    }
    
    const url = `${this.apiUrl}/nombre/vigente?${params}`;
    
    console.log('🔍 Buscando versión vigente por nombre:', nombreDocumento);

    return this.http.get(url, {
      withCredentials: true
    });
  }

  /**
   * Descargar documento vigente renderizado por nombre de documento.
   * Busca primero el documento vigente, luego descarga renderizado.
   * 
   * @param nombreDocumento Nombre del documento (ej: "Liderazgo y Compromiso", "Acta de Comité")
   * @param tipoSalida 'pdf' o 'word'
   * @returns Observable<HttpResponse> con blob
   */
  descargarDocumentoVigenteporNombre(
    nombreDocumento: string,
    tipoSalida?: 'pdf' | 'word',
    numeroComite?: number,
  ): Observable<any> {
    // Primero obtener la versión vigente por nombre
    return this.getVersionVigentePorNombre(nombreDocumento, numeroComite).pipe(
      switchMap((versionData: any) => {
        if (!versionData?.id) {
          return throwError(() => new Error(`No se encontró versión vigente para: ${nombreDocumento}`));
        }
        // Luego descargar esa versión renderizada
        return this.descargarDocumentoVersionRendizado(versionData.id, tipoSalida, numeroComite);
      }),
      catchError((error: any) => {
        console.error('Error descargando documento vigente por nombre:', error);
        return throwError(() => error);
      })
    );
  }
}